import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useSelector } from 'react-redux'
import {
  Tabs,
  Tab,
  Box,
  IconButton,
  CircularProgress,
  Button
} from '@mui/material'
import { ChevronLeft, ChevronRight, Lock, LockOpen } from '@mui/icons-material'
import styled from 'styled-components'
import PlaceHolderImg from '../assets/images/no-image-found.jpeg'
import Image from 'pages/Customer/CustomerDetails/EditView/DragAndDrop/components/Image'
import MaskedImage from './MaskedImage'
import configs from 'config/environment'
import { FacturaCheckModal } from 'components'
import RemovedImagePlaceholder from './RemovedImagePlaceholder'
import OcrReprocessConfirmModal from './OcrReprocessConfirmModal'

const DEFAULT_TAB_DATA = [
  { label: 'Passport', item: null },
  { label: 'Card 1234', item: null }
]

const FULL_SIZE = { width: '100%', height: '100%' }

const ImageTabs = ({
  tabData = [],
  onUploadDoc,
  onOCR,
  isAssigningDocument = false,
  isBatchOCRDataLoading = false,
  controlledIndex,
  onTabChange,
  hideTabs = false,
  tabsOnly = false,
}) => {
  const [tabIndex, setTabIndex] = useState(0)
  const [cardImageIndex, setCardImageIndex] = useState(0)
  const [imageLoading, setImageLoading] = useState(false)
  const [facturaCheckModalOpen, setFacturaCheckModalOpen] = useState(false)
  const [isMasked, setIsMasked] = useState(true)
  const [redactionRefreshKey, setRedactionRefreshKey] = useState(0)
  const maskedImageRef = useRef(null)

  // --- Selectors ---
  const userData = useSelector(state => state?.auth?.data)
  const { imagePreview } = useSelector(state => state.customer)
  const unmaskedCards = useSelector(state => state.customer.unmaskedCards || {})

  // --- Auth ---
  const userRole = userData?.current_user?.affiliate_role || false
  const isAuthAccess = userRole === 'auth_access'
  const isAdmin = userRole === 'admin'
  const canReprocessOcr = isAdmin || isAuthAccess

  const [showOcrReprocessConfirm, setShowOcrReprocessConfirm] = useState(false)

  // --- Tab resolution ---
  const effectiveTabIndex =
    typeof controlledIndex === 'number' ? controlledIndex : tabIndex
  const dataToUse = tabData.length > 0 ? tabData : DEFAULT_TAB_DATA
  const currentTab = tabData[effectiveTabIndex] || {}
  // --- Current tab attributes ---
  const { credit_card_token, redaction_verified, redaction_message, encrypted_filename } =
    currentTab
  const currentCardAttributes = currentTab.cardData?.attributes || {}
  const currentCardId = currentCardAttributes.id
  const cardOcrStatus = currentCardAttributes.ocr_status
  const cardLuhnCheck = currentCardAttributes.luhn_check
  const cardBinCheck = currentCardAttributes.bin_check
  const cardRedactionVerified = currentCardAttributes.redaction_verified

  // --- Tab type flags ---
  const isCardTab =
    /^\d{4}$/.test(currentTab.label) || currentTab.label?.startsWith('Card')
  const isPassportTab = currentTab.label === 'Passport'
  const isBoardingPassTab = currentTab.label === 'Boarding Pass'
  const isCruiseTab = currentTab.label === 'Cruise ID'
  const isPosReceiptTab = currentTab.label === 'POS'
  const isFacturaTab = currentTab.label === 'Factura'
  const isCCSlipTab =
    currentTab.label?.startsWith('CC Slip') ||
    currentTab.documentType?.startsWith('CC Slip') ||
    currentTab.label === `CC Slip ${currentTab?.paymentMethodIndex + 1}` ||
    (currentTab?.last_digits &&
      currentTab.label?.includes(currentTab.last_digits)) ||
    currentTab.item?.gb_doc_type === 'credit_card_slip'

  const isFullHeightTab = isPosReceiptTab || isCCSlipTab || isFacturaTab

  const getFacturaTabStatusBg = facturaStatus => {
    const normalized = String(facturaStatus || '').trim().toLowerCase()
    if (normalized === 'validated') return '#4caf50' // green
    if (normalized === 'cancelled' || normalized === 'reject') return '#f44336' // red
    return undefined // default gray
  }

  // --- Images ---
  const currentImages = useMemo(() => {
    if (isCardTab && currentTab.cardData?.files?.length > 0) {
      return currentTab.cardData.files.filter(Boolean)
    }
    return currentTab.item ? [currentTab.item] : []
  }, [isCardTab, currentTab.cardData?.files, currentTab.item])

  const currentImage = currentImages[cardImageIndex] || null

  const isImageRemoved =
    currentImage?.is_removed === true ||
    currentImage?.file_obj?.is_removed === true
  const removedMessage =
    currentImage?.removed_message ||
    currentImage?.file_obj?.removed_message ||
    'Image was removed'

  const currentImageUrl =
    currentImage && !isImageRemoved
      ? currentImage?.file_obj?.document_revision_id ||
      currentImage?.document_revision_id ||
      currentImage?.id
      : null

  // --- Derived card state ---
  const isNewlyCategorized =
    isCardTab &&
    Boolean(
      currentImage?.new_relation_addition ||
      currentImage?.file_obj?.new_relation_addition
    )
  const hasRedactionPassed =
    isCardTab &&
    cardOcrStatus === '1' &&
    cardLuhnCheck === '1' &&
    cardBinCheck === '1' &&
    cardRedactionVerified === '1'

  const hasRedactionFailure =
    cardLuhnCheck === '0' ||
    cardBinCheck === '0' ||
    // eslint-disable-next-line eqeqeq
    credit_card_token == '' ||
    cardRedactionVerified === '0'

  // --- Refresh keys ---
  const currentCardRefreshKey = currentCardId
    ? unmaskedCards[currentCardId]?.refreshKey || 0
    : 0
  const imagePreviewRefreshKey = imagePreview?.refreshKey || 0
  const imageRefreshKey = isCardTab
    ? (currentCardRefreshKey || imagePreviewRefreshKey || 0) +
    redactionRefreshKey
    : imagePreviewRefreshKey
  const computedRefreshKey =
    imageRefreshKey > 0 ? Date.now() + imageRefreshKey : null

  // --- OCR ---
  const showOCRButton =
    isPassportTab ||
    isCardTab ||
    isBoardingPassTab ||
    isPosReceiptTab ||
    isCCSlipTab ||
    isCruiseTab ||
    isFacturaTab
  const isOcrDone = isCardTab && cardOcrStatus === '1'

  // --- Mask toggle visibility ---
  const showMaskToggle =
    isAuthAccess &&
    isCardTab

  // --- Effects ---
  useEffect(() => {
    setImageLoading(!!currentImageUrl)
  }, [currentImageUrl])

  useEffect(() => {
    if (isCardTab && currentCardId && currentImageUrl) {
      setRedactionRefreshKey(prev => prev + 1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardOcrStatus, cardLuhnCheck, cardBinCheck, cardRedactionVerified])

  useEffect(() => {
    if (cardImageIndex >= currentImages.length && currentImages.length > 0) {
      setCardImageIndex(0)
    }
  }, [currentImages.length, cardImageIndex])

  // --- Handlers ---

  const handleTabChange = useCallback(
    (_, newValue) => {
      if (typeof onTabChange === 'function') {
        onTabChange(newValue)
      } else {
        setTabIndex(newValue)
      }
      setCardImageIndex(0)
    },
    [onTabChange]
  )

  const handlePrevImage = useCallback(() => {
    if (currentImages.length > 1) {
      setCardImageIndex(prev =>
        prev === 0 ? currentImages.length - 1 : prev - 1
      )
    }
  }, [currentImages.length])

  const handleNextImage = useCallback(() => {
    if (currentImages.length > 1) {
      setCardImageIndex(prev =>
        prev === currentImages.length - 1 ? 0 : prev + 1
      )
    }
  }, [currentImages.length])

  const handleImageLoad = useCallback(() => setImageLoading(false), [])
  const handleImageError = useCallback(() => setImageLoading(false), [])
  const handleFacturaCheck = useCallback(
    () => setFacturaCheckModalOpen(true),
    []
  )
  const handleCloseModal = useCallback(
    () => setFacturaCheckModalOpen(false),
    []
  )

  const handleMaskToggleClick = useCallback(e => {
    e.stopPropagation()
    maskedImageRef.current?.handleToggleClick?.(e)
  }, [])

  // --- Render helpers ---

  const renderCardTabImage = () => {
    const imageKey = `${currentImageUrl}-${imageRefreshKey}`
    const commonAlt = currentImage?.file_name || ''

    const maskedImageBaseProps = {
      isCardTab,
      url: currentImageUrl,
      item: currentImage,
      ...FULL_SIZE,
      alt: commonAlt,
      cardId: currentCardId,
      imageIndex: cardImageIndex,
      refreshKey: computedRefreshKey,
      onMaskStateChange: setIsMasked,
      relockWhenUnmaskCleared: true,
      showRedactFail: hasRedactionFailure,
      redactFailMessage:
        redaction_message?.trim() ||
        currentCardAttributes.redaction_message?.trim() ||
        ''
    }

    // Auth user + redaction NOT verified + (not newly categorized OR OCR done)
    if (
      isAuthAccess &&
      // eslint-disable-next-line eqeqeq
      cardRedactionVerified == '0' &&
      (!isNewlyCategorized || cardOcrStatus === '1')
    ) {
      return (
        <MaskedImage
          ref={maskedImageRef}
          key={`auth-failed-${imageKey}`}
          {...maskedImageBaseProps}
        />
      )
    }

    // Auth user (redaction verified or pending)
    if (isAuthAccess) {
      return (
        <MaskedImage
          ref={maskedImageRef}
          key={`auth-${imageKey}`}
          {...maskedImageBaseProps}
          disableVisualMask
        />
      )
    }

    // Non-auth: newly categorized → plain image
    if (isNewlyCategorized) {
      return (
        <Image
          key={`image-tab-${imageKey}`}
          item={currentImage}
          url={currentImageUrl}
          alt={commonAlt}
          {...FULL_SIZE}
        />
      )
    }

    // Non-auth: all OCR validations passed → redacted version (no overlay)
    if (hasRedactionPassed) {
      return (
        <Image
          key={`image-tab-${imageKey}`}
          item={currentImage}
          url={currentImageUrl}
          alt={commonAlt}
          {...FULL_SIZE}
          shouldMask
          cardId={currentCardId}
          refreshKey={computedRefreshKey}
        />
      )
    }

    // Non-auth: reopen / validation failed → blur + LOCKED overlay
    return (
      <MaskedImage
        ref={maskedImageRef}
        key={`masked-${imageKey}`}
        {...maskedImageBaseProps}
      />
    )
  }

  const renderTabImage = () => {
    if (isCardTab) return renderCardTabImage()

    return (
      <Image
        key={`image-tab-${currentImageUrl}-${imageRefreshKey}`}
        url={currentImageUrl}
        alt={currentImage?.file_name || ''}
        {...FULL_SIZE}
      />
    )
  }

  // --- JSX ---

  return (
    <Box sx={{ width: '100%' }}>
      {!hideTabs && (
        <StyledTabs value={effectiveTabIndex} onChange={handleTabChange}>
          {dataToUse.map((tab, idx) => {
            const tabStatusBg =
              tab?.label === 'Factura'
                ? getFacturaTabStatusBg(tab?.factura_status)
                : undefined

            return (
              <StyledTab
                key={
                  tab.key ||
                  `${tab.purchaseId || 'tab'}-${tab.paymentMethodIndex ?? idx}-${tab.documentType || tab.label}`
                }
                label={tab.label}
                active={effectiveTabIndex === idx ? 1 : 0}
                $statusBg={tabStatusBg}
              />
            )
          })}
        </StyledTabs>
      )}
      {!hideTabs && <TabsDivider />}

      {tabsOnly ? null : (
        <>
          <ImageContainer fullHeight={isFullHeightTab}>
            {isAssigningDocument && (
              <LoadingContainer>
                <CircularProgress size={40} sx={{ color: '#F6C44B' }} />
              </LoadingContainer>
            )}
            {currentImage ? (
              <>
                {isImageRemoved ? (
                  <RemovedImagePlaceholder message={removedMessage} />
                ) : (
                  <>
                    {imageLoading && (
                      <LoadingContainer>
                        <CircularProgress size={40} sx={{ color: '#F6C44B' }} />
                      </LoadingContainer>
                    )}
                    {currentImages.length > 1 && (
                      <NavigationContainer>
                        <NavButton onClick={handlePrevImage}>
                          <ChevronLeft />
                        </NavButton>
                        <ImageCounter>
                          {cardImageIndex + 1} / {currentImages.length}
                        </ImageCounter>
                        <NavButton onClick={handleNextImage}>
                          <ChevronRight />
                        </NavButton>
                      </NavigationContainer>
                    )}

                    <HiddenImage
                      key={`hidden-${currentImageUrl}-${imageRefreshKey}`}
                      src={`${configs.apiUrl}/index.php?entryPoint=Image&download=0&preview=0&file=${currentImageUrl}${imageRefreshKey > 0
                        ? `&_t=${Date.now() + imageRefreshKey}`
                        : ''
                        }`}
                      onLoad={handleImageLoad}
                      onError={handleImageError}
                      alt=""
                    />
                    <ImageWrapper
                      imageLoading={imageLoading}
                      fullHeight={isFullHeightTab}
                    >
                      {renderTabImage()}
                    </ImageWrapper>
                  </>
                )}

                {/* {isFacturaTab && (
                  <FacturaCheckContainer>
                    <FacturaCheckButton
                      onClick={handleFacturaCheck}
                      variant="outlined"
                      size="small"
                    >
                      Factura Check
                    </FacturaCheckButton>
                  </FacturaCheckContainer>
                )} */}
              </>
            ) : (
              <PlaceholderBox>
                <PlaceholderImg src={PlaceHolderImg} alt="No image available" />
              </PlaceholderBox>
            )}
            {showMaskToggle && (
              <>
                <MaskToggleButton
                  onClick={handleMaskToggleClick}
                  aria-label={isMasked ? 'Show image' : 'Hide image'}
                  title={
                    hasRedactionFailure
                      ? 'Redact Failure'
                      : 'Click to mask image'
                  }
                >
                  {isMasked ? (
                    <LockIcon
                      fontSize="small"
                      color={hasRedactionFailure ? 'red' : '#1565c0'}
                    />
                  ) : (
                    <LockOpenIcon
                      fontSize="small"
                      color={hasRedactionFailure ? 'red' : '#1565c0'}
                    />
                  )}
                </MaskToggleButton>
                {/* {redaction_verified === '0' && !!redaction_message?.trim() && (
                  <RedactionMessage>{redaction_message}</RedactionMessage>
                )} */}
              </>
            )}
          </ImageContainer>
          <Footer>
            <FooterButton
              onClick={() => onUploadDoc(currentTab)}
              disabled={isAssigningDocument}
            >
              {isAssigningDocument ? 'Assigning...' : 'Upload Doc'}
            </FooterButton>
            {showOCRButton && (
              <span
                title={
                  isOcrDone && !canReprocessOcr ? 'OCR already completed' : ''
                }
              >
                <FooterButton
                  onClick={() => {
                    if (isOcrDone) {
                      if (canReprocessOcr) {
                        setShowOcrReprocessConfirm(true)
                      }
                      return
                    }
                    onOCR(currentTab.label, currentTab)
                  }}
                  disabled={
                    isBatchOCRDataLoading || (isOcrDone && !canReprocessOcr)
                  }
                >
                  {isBatchOCRDataLoading
                    ? 'Processing...'
                    : isOcrDone
                      ? canReprocessOcr
                        ? 'Re-run OCR'
                        : 'OCR Completed'
                      : 'OCR'}
                </FooterButton>
              </span>
            )}
          </Footer>

          {/* <FacturaCheckModal
            open={facturaCheckModalOpen}
            onClose={handleCloseModal}
            purchaseId={currentTab.purchaseId}
          /> */}

          <OcrReprocessConfirmModal
            open={showOcrReprocessConfirm}
            cardLabel={currentTab.label}
            onClose={() => setShowOcrReprocessConfirm(false)}
            onConfirm={() => {
              setShowOcrReprocessConfirm(false)
              onOCR(currentTab.label, currentTab)
            }}
          />
        </>
      )}
    </Box>
  )
}

const StyledTabs = styled(Tabs)`
  min-height: 0 !important;

  .MuiTabs-flexContainer {
    min-height: 0;
    flex-wrap: wrap;
    gap: 5px;
  }

  .MuiTabs-indicator {
    display: none;
  }
`

const StyledTab = styled(Tab)`
  background: ${({ $statusBg }) => ($statusBg ? $statusBg : '#e5e5e5')} !important;
  color: ${({ $statusBg }) => ($statusBg ? '#fff' : '#333')} !important;
  font-size: 14px;
  font-weight: 400;
  min-width: 138px;
  margin: 0;
  text-transform: none;
  border: none;

  &.MuiTab-root {
    min-height: 22px;
    line-height: 22px;
    padding: 0 12px;
    border: none;
    background: ${({ $statusBg }) => ($statusBg ? $statusBg : '#e5e5e5')} !important;
    color: ${({ $statusBg }) => ($statusBg ? '#fff' : '#333')} !important;
    min-width: 138px;
    font-size: 14px;
    font-weight: 400;
    text-transform: none;
  }

  &.Mui-selected,
  &.Mui-selected.MuiTab-root {
    background: ${({ $statusBg }) => ($statusBg ? $statusBg : '#f6c44b')} !important;
    color: #fff !important;
  }
`

const TabsDivider = styled.div`
  width: 100%;
  border-bottom: 1px solid #eeeeee;
`

const FacturaCheckButton = styled(Button)`
  background: #e5e5e5;
  color: #333;
  font-size: 14px;
  border-radius: 4px;
  text-transform: none;
  border: 1px solid #ccc;
  margin-right: 5px;
`

const ImageContainer = styled(Box).withConfig({
  shouldForwardProp: prop => prop !== 'isCreditCard'
})`
  height: ${({ fullHeight }) => (fullHeight ? 'calc(100vh - 180px)' : '335px')};
  position: relative;
  ${props =>
    props.isCreditCard &&
    `
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    -webkit-user-drag: none;
    -khtml-user-drag: none;
    -moz-user-drag: none;
    -o-user-drag: none;
    user-drag: none;
    -webkit-touch-callout: none;
  `}
`

const PlaceholderBox = styled(Box)`
  background: rgb(247, 247, 247);
  padding: 10px;
  margin: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
`

const PlaceholderImg = styled.img`
  width: 150px;
  height: 140px;
  opacity: 0.08;
`

const Footer = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  width: 100%;
`

const FooterButton = styled.span`
  color: #5eba00 !important;
  font-size: 14px;
  cursor: pointer;

  ${({ disabled }) =>
    disabled &&
    `
    color: #9e9e9e !important;
    cursor: not-allowed;
    pointer-events: none;
  `}
`

const NavigationContainer = styled(Box)`
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  align-items: center;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 20px;
  padding: 5px 10px;
  z-index: 10;
`

const NavButton = styled(IconButton)`
  color: white !important;
  padding: 4px !important;

  &:hover {
    background: rgba(255, 255, 255, 0.1) !important;
  }
`

const ImageCounter = styled.span`
  color: white;
  font-size: 12px;
  margin: 0 8px;
  font-weight: 500;
`

const LoadingContainer = styled(Box)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const ImageWrapper = styled.div`
  width: 100%;
  max-height: ${({ fullHeight }) =>
    fullHeight ? 'calc(100vh - 220px)' : '334px'};
  object-fit: contain;
  height: 100%;
  overflow: auto;
`

const HiddenImage = styled.img`
  display: none;
`

const FacturaCheckContainer = styled(Box)`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 10;
`

const MaskToggleButton = styled(IconButton)`
  position: absolute;
  bottom: 5px;
  left: 5px;
  background: rgba(255, 255, 255, 0.9) !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
  z-index: 10 !important;
  padding: 5px !important;
`

const LockIcon = styled(Lock)`
  color: ${({ color }) => color || '#1565c0'};
  width: 15px !important;
  height: 15px !important;
`

const LockOpenIcon = styled(LockOpen)`
  color: ${({ color }) => color || '#1565c0'};
  width: 15px !important;
  height: 15px !important;
`

const RedactionMessage = styled.span`
  position: absolute;
  bottom: 0;
  left: 43px;
  color: red;
  font-size: 17px;
  background-color: white;
  padding: 6px 5px 0 5px;
  border-radius: 5px;
  margin-bottom: 5px;
`

export default ImageTabs
